import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../inJoy-components/Icon/Icon";

interface CourtLinesUploadProps {
	file: File | null;
	onChange: (file: File | null) => void;
	/** Base64 string of existing calibration from the agent, if any */
	existingCalibration?: string | null;
}

const formatFileSize = (bytes: number): string => {
	if (bytes < 1024) return `${bytes}B`;
	return `${Math.round(bytes / 1024)}KB`;
};

const PROGRESS_DURATION = 400;

const FileCard = ({
	name,
	extension,
	size,
	onRemove,
	isAnimating,
}: {
	name: string;
	extension: string;
	size: number;
	onRemove: () => void;
	isAnimating?: boolean;
}) => (
	<div
		className={`w-full rounded-[4px] ${isAnimating ? "relative overflow-hidden" : ""}`}
	>
		<div className="border border-[#d4d4d4] border-solid flex gap-[16px] items-center p-[16px] rounded-[4px] w-full">
			<div className="border border-[#d4d4d4] border-solid flex items-start p-[8px] rounded-[4px] shrink-0">
				<Icon iconName="document_outline" size="Large" color="#404040" />
			</div>
			<div className="flex flex-1 flex-col gap-0 items-start justify-center min-w-0">
				<p className="text-[14px] leading-[20px] text-[#1f2630] truncate w-full">
					{name}
				</p>
				<p className="text-[12px] leading-[16px] text-[#78818e]">
					{extension} &#8226; {formatFileSize(size)}
				</p>
			</div>
			<button type="button" className="p-[4px] shrink-0" onClick={onRemove}>
				<Icon iconName="cross_outline" size="Large" color="#404040" />
			</button>
		</div>
		{isAnimating && (
			<div className="absolute bottom-0 left-0 right-0 h-[6px]">
				<div
					className="h-full bg-[#0060e5] rounded-br-full rounded-tr-full animate-[progress-fill_0.4s_ease-out_forwards]"
					style={{ width: "100%" }}
				/>
			</div>
		)}
	</div>
);

const base64ByteSize = (b64: string): number => {
	const padding = (b64.match(/=+$/) || [""])[0].length;
	return Math.floor((b64.length * 3) / 4) - padding;
};

export const CourtLinesUpload = ({
	file,
	onChange,
	existingCalibration,
}: CourtLinesUploadProps) => {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);
	const animTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (animTimerRef.current) clearTimeout(animTimerRef.current);
		};
	}, []);

	const acceptFile = useCallback(
		(f: File) => {
			if (!f.name.endsWith(".npy")) return;
			setIsAnimating(true);
			onChange(f);
			animTimerRef.current = setTimeout(
				() => setIsAnimating(false),
				PROGRESS_DURATION,
			);
		},
		[onChange],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);
			const droppedFile = e.dataTransfer.files[0];
			if (droppedFile) acceptFile(droppedFile);
		},
		[acceptFile],
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files?.[0];
		if (selected) acceptFile(selected);
		if (inputRef.current) inputRef.current.value = "";
	};

	const openFilePicker = () => inputRef.current?.click();

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			openFilePicker();
		}
	};

	const hasExisting = !file && !!existingCalibration;

	return (
		<div className="flex flex-col gap-[8px] items-start w-full">
			<p className="flex items-end gap-[2px] w-full Subtitle2 text-[#404040]">
				{t("events.create_dialog.calibration_label")}
			</p>

			{hasExisting ? (
				<FileCard
					name={t("events.create_dialog.calibration_existing")}
					extension="NPY"
					size={base64ByteSize(existingCalibration)}
					onRemove={() => onChange(null)}
				/>
			) : file ? (
				<FileCard
					name={file.name.replace(/\.[^.]+$/, "")}
					extension={file.name.split(".").pop()?.toUpperCase() || ""}
					size={file.size}
					onRemove={() => onChange(null)}
					isAnimating={isAnimating}
				/>
			) : (
				<button
					type="button"
					className={`w-full h-[157px] rounded-[4px] bg-white cursor-pointer ${isDragging ? "ring-2 ring-[#0060e5]" : ""}`}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onClick={openFilePicker}
					onKeyDown={handleKeyDown}
				>
					<div className="flex flex-col gap-0 items-center justify-center border border-dashed border-[#d4d4d4] rounded-[4px] px-[16px] py-[24px] size-full">
						<Icon iconName="cloud_arrow_up" size="XXXLarge" color="#404040" />
						<p className="text-[14px] font-medium leading-[1.5] text-[#1f2630] text-center whitespace-nowrap">
							{t("events.create_dialog.calibration_drop")}
						</p>
					</div>
				</button>
			)}

			<input
				ref={inputRef}
				type="file"
				accept=".npy"
				className="hidden"
				onChange={handleInputChange}
			/>

			<p className="text-[14px] font-normal leading-[1.4] tracking-[0.25px] text-[#6d6d6d] w-full">
				{t("events.create_dialog.calibration_hint")}
			</p>
		</div>
	);
};
